import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Search,
  Plus,
  Pencil,
  Trash2,
  Star,
  CheckCircle2,
  LayoutTemplate,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  useLazyGetEmailLayoutsQuery,
  useDeleteEmailLayoutMutation,
  useSetDefaultEmailLayoutMutation,
} from "../../../redux/actions/Admin/ServiceAdmin";

import { IEmailLayout } from "../../../types/Request/AdminRequest";

const EmailLayoutsScreen: React.FC = () => {
  const navigate = useNavigate();

  const [getEmailLayouts, { data, isLoading, isFetching }] =
    useLazyGetEmailLayoutsQuery();

  const [deleteEmailLayout, { isLoading: isDeleting }] =
    useDeleteEmailLayoutMutation();

  const [setDefaultEmailLayout, { isLoading: isSettingDefault }] =
    useSetDefaultEmailLayoutMutation();

  const [layouts, setLayouts] = useState<IEmailLayout[]>([]);
  const [search, setSearch] = useState("");

  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    getEmailLayouts();
  }, [getEmailLayouts]);

  useEffect(() => {
    if (data) {
      setLayouts(data);
    }
  }, [data]);

  const filteredLayouts = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) {
      return layouts;
    }

    return layouts.filter(
      (item) =>
        item.name?.toLowerCase().includes(value) ||
        item.description?.toLowerCase().includes(value) ||
        item.brandingProfileName?.toLowerCase().includes(value),
    );
  }, [layouts, search]);

  const handleSetDefault = async (id: number) => {
    try {
      await setDefaultEmailLayout(id).unwrap();

      getEmailLayouts();
    } catch (error) {
      console.error("Failed to set default layout:", error);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) {
      return;
    }

    try {
      await deleteEmailLayout(deleteId).unwrap();

      setShowDeleteDialog(false);
      setDeleteId(null);

      getEmailLayouts();
    } catch (error) {
      console.error("Failed to delete email layout:", error);
    }
  };

  if (isLoading || isFetching) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Email Layouts</h2>

          <p className="mt-1 text-sm text-slate-500">
            Manage reusable email layouts and branding.
          </p>
        </div>
      </div>

      {/* Search */}

      <div className="relative w-full sm:w-80">
        <Search
          size={17}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />

        <input
          type="text"
          placeholder="Search layouts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
        />
      </div>

      {/* Table */}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Layout
                </th>

                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Branding
                </th>

                <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Default
                </th>

                <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Status
                </th>

                <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredLayouts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center">
                    <div className="flex flex-col items-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <LayoutTemplate size={22} />
                      </div>

                      <p className="mt-3 text-sm font-medium text-slate-700">
                        No email layouts found
                      </p>

                      <p className="mt-1 text-xs text-slate-400">
                        Create a layout to get started.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredLayouts.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-slate-100 transition hover:bg-slate-50"
                  >
                    {/* Layout */}

                    <td className="px-5 py-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">
                            {item.name}
                          </span>

                          {item.isDefault && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-600">
                              <Star size={10} />
                              Default
                            </span>
                          )}
                        </div>

                        <p className="mt-1 max-w-md truncate text-xs text-slate-400">
                          {item.description || "—"}
                        </p>
                      </div>
                    </td>

                    {/* Branding */}

                    <td className="px-5 py-4 text-sm text-slate-600">
                      {item.brandingProfileName || "—"}
                    </td>

                    {/* Default */}

                    <td className="px-5 py-4 text-center">
                      {item.isDefault ? (
                        <CheckCircle2
                          size={18}
                          className="mx-auto text-[#2E7D32]"
                        />
                      ) : (
                        <button
                          type="button"
                          disabled={isSettingDefault}
                          onClick={() => handleSetDefault(item.id)}
                          className="text-xs font-medium text-slate-400 transition hover:text-[#2E7D32] disabled:opacity-50"
                        >
                          Set Default
                        </button>
                      )}
                    </td>

                    {/* Status */}

                    <td className="px-5 py-4 text-center">
                      <span
                        className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                          item.isActive
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-600"
                        }`}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* Actions */}

                    <td className="px-5 py-4">
                      <div className="flex justify-center gap-4">
                        <button
                          type="button"
                          title="Edit"
                          onClick={() =>
                            navigate(
                              `/admin/adminemailtemplates/layouts/edit/${item.id}`,
                            )
                          }
                        >
                          <Pencil
                            size={17}
                            className="text-green-600 transition hover:scale-110"
                          />
                        </button>

                        {!item.isDefault && (
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => {
                              setDeleteId(item.id);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2
                              size={17}
                              className="text-red-500 transition hover:scale-110"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Dialog */}

      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">
              Delete Email Layout
            </h3>

            <p className="mt-2 text-sm text-slate-500">
              Are you sure you want to delete this email layout?
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteId(null);
                }}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={isDeleting}
                onClick={handleDelete}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {isDeleting && <Loader2 size={15} className="animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailLayoutsScreen;
